/**
 * TrackedQuestionsPanel - AI-driven questionnaire tracker for the live meeting right rail.
 *
 * Shows deal questionnaire fields grouped by section → question. As the meeting progresses
 * the background useDealCoaching hook populates suggested questions and captured
 * answers, which this panel renders reactively.
 *
 * Gate (non-aiEnabled) fields render as context labels (no status dot).
 * Live-captured answers display with a "New" chip; DB answers display muted.
 * When a live capture overwrites a DB answer, the previous value shows as "Was: …".
 *
 * This component is purely presentational - all AI state lives in LiveMeetingPage
 * via useDealCoaching so it never interferes with the transcript rendering path.
 */

import { isCompositeControl } from '@protoss/shared';
import type {
  ExtractChildField,
  ExtractQuestion,
  ExtractSection,
  ExtractSectionField,
} from '../hooks/useDealExtract';

export interface TrackedQuestionsPanelProps {
  /** Questionnaire sections with per-field AI extraction state. */
  sections: ExtractSection[];
  totalCount: number;
  answeredCount: number;
  isLoading: boolean;
  /** True while an AI extraction call is in-flight - shows a pulsing indicator. */
  isInferring?: boolean;
  /** When true, no deal is linked - show a prompt instead of questions. */
  noDeal?: boolean;
  /** Save-on-end status message shown below the progress bar. */
  saveFeedback?: string | null;
}

export function TrackedQuestionsPanel({
  sections,
  totalCount,
  answeredCount,
  isLoading,
  isInferring = false,
  noDeal = false,
  saveFeedback,
}: TrackedQuestionsPanelProps) {
  if (noDeal) {
    return (
      <div className="lm-tracked__panel-body">
        <p className="lm-tracked__empty">Link a deal to track discovery progress.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="lm-tracked__panel-body">
        <p className="lm-tracked__empty">Loading questionnaire…</p>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="lm-tracked__panel-body">
        <p className="lm-tracked__empty">No AI-enabled questions found in the default template.</p>
      </div>
    );
  }

  const progressPct = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;

  return (
    <div className="lm-tracked__panel-body">
      {/* Discovery progress bar */}
      <div className="lm-tracked__progress">
        <div className="lm-tracked__progress-header">
          <span className="lm-tracked__progress-label-group">
            <span className="lm-tracked__progress-label">Discovery progress</span>
            {isInferring && (
              <div className="lm-inferring" aria-live="polite" aria-label="AI is inferring">
                <span className="lm-inferring__dot" aria-hidden="true" />
                <span className="lm-inferring__dot" aria-hidden="true" />
                <span className="lm-inferring__dot" aria-hidden="true" />
              </div>
            )}
          </span>
          <span className="lm-tracked__progress-count">
            {answeredCount} / {totalCount}
          </span>
        </div>
        <div
          className="lm-tracked__progress-bar"
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Discovery progress"
        >
          <div className="lm-tracked__progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        {saveFeedback && <p className="lm-tracked__save-feedback">{saveFeedback}</p>}
      </div>

      {/* Section groups */}
      <div className="lm-tracked__sections">
        {sections.map((section) => (
          <SectionGroup key={section.sectionKey} section={section} />
        ))}
      </div>
    </div>
  );
}

// ── Section group ─────────────────────────────────────────────────────────

function SectionGroup({ section }: { section: ExtractSection }) {
  const allFields = section.groups.flatMap((q) => q.fields);
  const aiFields = allFields.filter((f) => !f.isGate);
  const answeredInSection = aiFields.filter((f) => f.state.status === 'answered').length;

  return (
    <section className="lm-tracked__section" aria-label={section.label}>
      <div className="lm-tracked__section-header">
        <span className="lm-tracked__section-label">{section.label}</span>
        <span className="lm-tracked__section-count" aria-label="answered / total">
          {answeredInSection}/{aiFields.length}
        </span>
      </div>
      {section.groups.map((question) => (
        <QuestionGroup key={question.groupKey} question={question} />
      ))}
    </section>
  );
}

// ── Question group ────────────────────────────────────────────────────────

function QuestionGroup({ question }: { question: ExtractQuestion }) {
  if (question.fields.length === 0) return null;

  return (
    <div className="lm-tracked__question">
      <div className="lm-tracked__question-label">{question.label}</div>
      <ul className="lm-tracked__fields">
        {question.fields.map((field) => (
          <FieldRow key={field.fieldKey} field={field} />
        ))}
      </ul>
    </div>
  );
}

// ── Answer formatter ──────────────────────────────────────────────────────

/**
 * Converts a raw AI-extracted answer string to a human-readable display value.
 * Handles all uiControl types used in the deal questionnaire, including structured
 * compound types (checklist, select_matrix, radio_text_list) that store JSON objects.
 */
function formatAnswer(
  answer: string,
  uiControl: string,
  options: ExtractSectionField['options']
): string {
  // ── Label lookups ──────────────────────────────────────────────────────
  const itemOptions = options.filter((o) => !o.optionType || o.optionType === 'item');
  const labelByValue = new Map(options.map((o) => [o.value, o.label]));

  // ── Single-select controls (option lookup) ─────────────────────────────
  if (uiControl === 'select' || uiControl === 'stack_option' || uiControl === 'pill_radio') {
    return labelByValue.get(answer) ?? answer;
  }

  // ── Multi-select controls ──────────────────────────────────────────────
  if (uiControl === 'pill_select' || uiControl === 'multi_select') {
    try {
      const parsed: unknown = JSON.parse(answer);
      if (Array.isArray(parsed)) {
        const labels = parsed.map((v) =>
          typeof v === 'string' ? (labelByValue.get(v) ?? v) : String(v)
        );
        if (labels.length > 0) return labels.join(', ');
      }
    } catch {
      // not JSON array - fall through to raw
    }
    return answer;
  }

  // ── Boolean ────────────────────────────────────────────────────────────
  if (uiControl === 'boolean') {
    if (answer === 'true' || answer === '1') return 'Yes';
    if (answer === 'false' || answer === '0') return 'No';
    return answer;
  }

  // ── Currency ───────────────────────────────────────────────────────────
  if (uiControl === 'currency') {
    // Answer may be a plain number string or a JSON {amount, currency} object string.
    let amount = answer;
    let currencyCode = 'USD';
    try {
      const parsed: unknown = JSON.parse(answer);
      if (parsed !== null && typeof parsed === 'object' && 'amount' in parsed) {
        const cv = parsed as Record<string, unknown>;
        amount = String(cv['amount'] ?? '');
        if (typeof cv['currency'] === 'string') currencyCode = cv['currency'];
      }
    } catch {
      // plain string - use as-is
    }
    const num = Number(amount);
    if (!Number.isNaN(num)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 0,
      }).format(num);
    }
    return amount;
  }

  // ── Number / slider ────────────────────────────────────────────────────
  if (uiControl === 'number' || uiControl === 'slider') {
    const num = Number(answer);
    if (!Number.isNaN(num)) {
      return new Intl.NumberFormat('en-US').format(num);
    }
    return answer;
  }

  // ── Date ───────────────────────────────────────────────────────────────
  if (uiControl === 'date') {
    try {
      const d = new Date(answer);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
    } catch {
      // not a valid date
    }
    return answer;
  }

  // ── Checklist: { rowKey: { applies: bool, selection: string | null } } ──
  if (uiControl === 'checklist') {
    try {
      const parsed: unknown = JSON.parse(answer);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        const data = parsed as Record<
          string,
          { applies?: boolean | null; selection?: string | null }
        >;
        const choiceByValue = new Map(
          options.filter((o) => o.optionType === 'choice').map((o) => [o.value, o.label])
        );
        const applied = itemOptions
          .filter((o) => data[o.value]?.applies === true)
          .map((o) => {
            const sel = data[o.value]?.selection;
            const selLabel = sel ? choiceByValue.get(sel) : null;
            return selLabel ? `${o.label} (${selLabel})` : o.label;
          });
        if (applied.length > 0) return applied.join(', ');
        // All explicitly false → "None"
        const allFalse = itemOptions.every((o) => data[o.value]?.applies === false);
        if (allFalse) return 'None';
      }
    } catch {
      // not JSON - fall through
    }
    return answer;
  }

  // ── Select matrix: { rowKey: { tool: string, plan: string } } ──────────
  if (uiControl === 'select_matrix') {
    try {
      const parsed: unknown = JSON.parse(answer);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        const data = parsed as Record<string, { tool?: string | null; plan?: string | null }>;
        const lines = itemOptions
          .filter((o) => data[o.value]?.tool)
          .map((o) => {
            const entry = data[o.value];
            const planLabel = entry?.plan ? (labelByValue.get(entry.plan) ?? entry.plan) : null;
            return planLabel
              ? `${o.label}: ${entry!.tool} (${planLabel})`
              : `${o.label}: ${entry!.tool}`;
          });
        if (lines.length > 0) return lines.join(', ');
      }
    } catch {
      // not JSON - fall through
    }
    return answer;
  }

  // ── Radio text list: { rowKey: { selection: string, text: string | null } } ─
  if (uiControl === 'radio_text_list') {
    try {
      const parsed: unknown = JSON.parse(answer);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        const data = parsed as Record<string, { selection?: string | null; text?: string | null }>;
        const lines = itemOptions
          .filter((o) => data[o.value]?.selection)
          .map((o) => {
            const entry = data[o.value];
            const selLabel = entry?.selection
              ? (labelByValue.get(entry.selection) ?? entry.selection)
              : null;
            return selLabel ? `${o.label}: ${selLabel}` : o.label;
          });
        if (lines.length > 0) return lines.join(', ');
      }
    } catch {
      // not JSON - fall through
    }
    return answer;
  }

  // ── Default (text, textarea, etc.) ────────────────────────────────────
  return answer;
}

// ── Compound field helpers ────────────────────────────────────────────────

interface CompoundItemDisplay {
  value: string;
  label: string;
  description: string | null;
  answer: string | null;
  source: 'db' | 'live' | null;
}

/**
 * Parses the JSON answer blob for a compound field and extracts a display
 * answer for each item option row.
 */
function extractCompoundItems(field: ExtractSectionField): CompoundItemDisplay[] {
  const itemOptions = field.options.filter((o) => !o.optionType || o.optionType === 'item');
  const { state, uiControl } = field;
  const source = state.status === 'answered' ? state.source : null;

  let parsed: Record<string, unknown> | null = null;
  if (state.status === 'answered') {
    try {
      const p: unknown = JSON.parse(state.answer);
      if (typeof p === 'object' && p !== null && !Array.isArray(p)) {
        parsed = p as Record<string, unknown>;
      }
    } catch {
      // not a JSON object - fall through, all items will be pending
    }
  }

  const choiceLabelByValue = new Map(
    field.options.filter((o) => o.optionType === 'choice').map((o) => [o.value, o.label])
  );
  const planLabelByValue = new Map(
    field.options.filter((o) => o.optionType === 'plan').map((o) => [o.value, o.label])
  );

  return itemOptions.map((item): CompoundItemDisplay => {
    if (!parsed) {
      return {
        value: item.value,
        label: item.label,
        description: item.description ?? null,
        answer: null,
        source: null,
      };
    }

    const entry = parsed[item.value];
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      return {
        value: item.value,
        label: item.label,
        description: item.description ?? null,
        answer: null,
        source: null,
      };
    }
    const e = entry as Record<string, unknown>;

    let answer: string | null = null;

    if (uiControl === 'radio_text_list') {
      const sel = typeof e.selection === 'string' ? e.selection : null;
      if (sel) {
        answer = choiceLabelByValue.get(sel) ?? sel;
      }
    } else if (uiControl === 'checklist') {
      const applies = typeof e.applies === 'boolean' ? e.applies : null;
      if (applies !== null) {
        const sel = typeof e.selection === 'string' ? e.selection : null;
        const selLabel = sel ? choiceLabelByValue.get(sel) : null;
        answer = applies ? (selLabel ? `Yes · ${selLabel}` : 'Yes') : 'No';
      }
    } else if (uiControl === 'select_matrix') {
      const tool = typeof e.tool === 'string' ? e.tool : null;
      if (tool) {
        const plan = typeof e.plan === 'string' ? e.plan : null;
        const planLabel = plan ? (planLabelByValue.get(plan) ?? plan) : null;
        answer = planLabel ? `${tool} · ${planLabel}` : tool;
      }
    }

    return {
      value: item.value,
      label: item.label,
      description: item.description ?? null,
      answer,
      source: answer ? source : null,
    };
  });
}

/** Expanded sub-list rendered inside a compound field's body. */
function CompoundItemList({ field }: { field: ExtractSectionField }) {
  const items = extractCompoundItems(field);
  const isLiveSource = field.state.status === 'answered' && field.state.source === 'live';

  return (
    <ul className="lm-tracked__compound-rows">
      {items.map((item) => (
        <li
          key={item.value}
          className={`lm-tracked__compound-row${item.answer ? ' lm-tracked__compound-row--answered' : ''}`}
        >
          <span className="lm-tracked__compound-row-icon" aria-hidden="true">
            {item.answer ? '✓' : '○'}
          </span>
          <span className="lm-tracked__compound-row-content">
            <span className="lm-tracked__compound-row-label">{item.label}</span>
            {item.description && !item.answer && (
              <span className="lm-tracked__compound-row-desc">{item.description}</span>
            )}
            {item.answer && (
              <span
                className={`lm-tracked__compound-row-answer${isLiveSource ? ' lm-tracked__compound-row-answer--live' : ' lm-tracked__compound-row-answer--db'}`}
              >
                {item.answer}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Field row ─────────────────────────────────────────────────────────────

function FieldRow({ field }: { field: ExtractSectionField }) {
  const { state } = field;
  const nestingClass = field.nestingLevel > 0 ? ' lm-tracked__field--nested' : '';
  const isCompound = isCompositeControl(field.uiControl);

  // Gate fields: render as a context label (no status dot, no tracking)
  if (field.isGate) {
    const gateAnswer =
      !isCompound && state.status === 'answered'
        ? formatAnswer(state.answer, field.uiControl, field.options)
        : undefined;

    return (
      <li className={`lm-tracked__field lm-tracked__field--gate${nestingClass}`}>
        <span className="lm-tracked__field-icon" aria-hidden="true">
          -
        </span>
        <div className="lm-tracked__field-body">
          <span className="lm-tracked__field-label">{field.label}</span>
          {gateAnswer && (
            <span className="lm-tracked__field-answer lm-tracked__field-answer--db">
              {gateAnswer}
            </span>
          )}
          {isCompound && <CompoundItemList field={field} />}
        </div>
      </li>
    );
  }

  // Tracked (aiEnabled) fields
  const { status } = state;
  const isLive = status === 'answered' && state.source === 'live';
  const isDb = status === 'answered' && state.source === 'db';

  // Non-compound: single answer string display
  const rawAnswer = !isCompound && status === 'answered' ? state.answer : undefined;
  const displayAnswer = rawAnswer
    ? formatAnswer(rawAnswer, field.uiControl, field.options)
    : undefined;
  const previousDbAnswer =
    !isCompound &&
    status === 'answered' &&
    state.source === 'live' &&
    state.previousDbAnswer != null
      ? formatAnswer(state.previousDbAnswer, field.uiControl, field.options)
      : undefined;

  return (
    <li className={`lm-tracked__field lm-tracked__field--${status}${nestingClass}`}>
      <span className="lm-tracked__field-icon" aria-hidden="true">
        {status === 'answered' ? '✓' : status === 'suggested' ? '●' : '○'}
      </span>
      <div className="lm-tracked__field-body">
        <span className="lm-tracked__field-label">{field.label}</span>
        {field.description && status !== 'answered' && !isCompound && (
          <span className="lm-tracked__field-description">{field.description}</span>
        )}
        {isCompound ? (
          <CompoundItemList field={field} />
        ) : (
          <>
            {status === 'answered' && displayAnswer && (
              <span
                className={`lm-tracked__field-answer${isLive ? ' lm-tracked__field-answer--live' : ''}${isDb ? ' lm-tracked__field-answer--db' : ''}`}
              >
                {displayAnswer}
              </span>
            )}
            {previousDbAnswer && (
              <span className="lm-tracked__field-answer-was">Was: {previousDbAnswer}</span>
            )}
          </>
        )}
        <InlineChildren field={field} />
      </div>
    </li>
  );
}

// ── Inline child answers ──────────────────────────────────────────────────────────

/**
 * Renders dependent child fields' answers inline under the parent field.
 * Only shows children whose conditions are currently met AND that have been answered.
 */
function InlineChildren({ field }: { field: ExtractSectionField }) {
  if (!field.children || field.children.length === 0) return null;

  const visibleChildren = field.children.filter(
    (
      c
    ): c is ExtractChildField & {
      state: {
        status: 'answered';
        answer: string;
        source: 'db' | 'live';
        previousDbAnswer?: string;
      };
    } => c.conditionMet && c.state.status === 'answered'
  );

  if (visibleChildren.length === 0) return null;

  return (
    <ul className="lm-tracked__field-children">
      {visibleChildren.map((child) => (
        <li key={child.fieldKey} className="lm-tracked__field-child">
          <span className="lm-tracked__field-child-label">{child.label}:</span>
          <span className="lm-tracked__field-child-answer">
            {formatAnswer(child.state.answer, child.uiControl, child.options)}
          </span>
        </li>
      ))}
    </ul>
  );
}
